'use client';

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import { useAppSelector } from '@/lib/redux/store';
import { Image, Nav, Offcanvas, Toast, ToastContainer } from 'react-bootstrap';
import Link from 'next/link';
import ToastMessage from '@/components/ToastMessage';
import { useToasts } from '@/hooks/toasts';
import { useAuth } from '@/hooks/auth';
import { useSelector } from 'react-redux';
import { Loading } from '@/components/Loading';
import CustomerOnboarding from '@/components/onboarding/CustomerOnboarding';

const AdminProvider = ({ children }) =>
{
  return <Admin>{children}</Admin>;
};

const Admin = ({ children }) =>
{
  const router = useRouter();
  const app = useAppSelector((state) => state.app);
  const persist = useSelector((state) => state._persist);
  const { user, roleActingAs, userEntityRoles } = app;

  const [logo, setLogo] = useState('/images/instant-polls-logo.jpg');
  const { logout, refreshLoggedInUser } = useAuth();
  const { toasts, removeToast } = useToasts();

  const entitySlug = roleActingAs?.entity?.slug;

  // Use asPath (resolved URL) instead of pathname (dynamic pattern)
  const pathname = router.asPath;

  const [showSidebar, setShowSidebar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(true);

  useEffect(() =>
  {
    if (!persist?.rehydrated) return;
    setIsLoading(false);
  }, [persist?.rehydrated]);

  useEffect(() =>
  {
    if (persist?.rehydrated && !user)
    {
      router.push('/login');
      return;
    }
    if (persist?.rehydrated && user && !roleActingAs)
    {
      router.push('/login');
      return;
    }
    if (roleActingAs?.role?.name === 'User') {
      logout('/login');
      return;
    }

    if ((roleActingAs?.expiresAt && new Date(roleActingAs.expiresAt) < new Date()) || roleActingAs?.expiresAt === undefined) {
      // refresh logged in user
      refreshLoggedInUser();
    }
    const entityLogo = roleActingAs?.entity?.logo;
    setLogo(entityLogo || '/images/instant-polls-logo.jpg');
  }, [logout, persist?.rehydrated, roleActingAs, router, user, refreshLoggedInUser]);

  // updated isActive
  const isActive = useCallback(
    (path) =>
    {
      if (!path) return false;

      // normalize trailing slash
      const normalized = path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;

      return pathname === normalized || pathname.startsWith(`${normalized}/`);
    },
    [pathname]
  );

  // const handleLogout = () => logout();
  const handleLogout = () =>
  {
    logout();
  };

  let sidebarLinks = [
    { label: 'Dashboard', href: `/dashboard`, icon: 'bi bi-speedometer2' },
    { label: 'Polls', href: `/${entitySlug}/polls`, icon: 'bi bi-bar-chart' },
    { label: 'Question Templates', href: `/poll-templates`, icon: 'bi bi-patch-question' },
    { label: 'Team', href: `/${entitySlug}/teams`, icon: 'bi bi-people' },    
    { label: 'Settings', href: `/${entitySlug}/settings`, icon: 'bi bi-sliders' },
    { label: 'My Account', href: '/account', icon: 'bi bi-person' },
    { label: 'Security', href: '/security', icon: 'bi bi-shield-lock' },
    { label: 'Billing', href: `/${entitySlug}/billing`, icon: 'bi bi-cash-coin' },
    { label: 'Tutorial', href: `/${entitySlug}/tutorial`, icon: 'bi bi-play-btn' },
    { label: 'Help Center', href: '/support', icon: 'bi bi-question-circle' },
    {
      label: 'Logout',
      href: '#',
      icon: 'bi bi-box-arrow-right',
      onClick: handleLogout,
    },
  ];

  if (userEntityRoles?.length > 1)
  {
    sidebarLinks.splice(sidebarLinks.length - 1, 0, {
      label: 'Switch Account',
      href: '/select-role',
      icon: 'bi bi-person-circle',
    });
  }

  if (roleActingAs?.role?.name === 'Admin')
  {
    // sidebarLinks = [
    //   ...sidebarLinks,
    //   {
    //     label: 'Admin Panel',
    //     href: `/${entitySlug}/admin`,
    //     icon: 'bi bi-shield-lock',
    //   },
    // ];
  } else if (roleActingAs?.role?.name === 'Basic')
  {
    sidebarLinks = sidebarLinks.filter(
      (link) =>
        link.label !== 'Help Center' &&
        link.label !== 'Test Drive' &&
        link.label !== 'Billing' &&
        link.label !== 'Admin Panel'
    );
  } else if (roleActingAs?.role?.name === 'Finance')
  {
    sidebarLinks = sidebarLinks.filter(
      (link) =>
        link.label !== 'Help Center' &&
        link.label !== 'Test Drive' &&
        link.label !== 'Team' &&
        link.label !== 'Polls' &&
        link.label !== 'Admin Panel'
    );
  }

  const handleSidebarClose = () => setShowSidebar(false);
  const handleSidebarShow = () => setShowSidebar(true);
  const pageRef = useRef(null);

  if (isLoading)
  {
    return <Loading loading={isLoading} />;
  }

  return (
    <div className="backend">
      {roleActingAs?.entity?.has_failed_payment && (
        <div className="alert alert-danger mb-0 text-center" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <strong>Payment Failed:</strong> Your last invoice payment was unsuccessful. Please update your payment method to avoid service interruption.
          <Link href={`/${entitySlug}/billing`} className="alert-link ms-2">
            Update Payment Method
          </Link>
        </div>
      )}
      <section className="d-flex position-relative">
        {/* Mobile Navbar */}
        <nav className="navbar navbar-light bg-light d-lg-none px-3 w-100">
          <button className="btn" type="button" onClick={handleSidebarShow}>
            <i className="bi bi-list fs-2"></i>
          </button>
          <Link className="navbar-brand ms-2 mobileResponse" href="/">
            <Image src={logo} alt="Logo" height={40} />
          </Link>
        </nav>

        {/* Sidebar Offcanvas */}
        <Offcanvas
          show={showSidebar}
          onHide={handleSidebarClose}
          responsive="lg"
          className="sticky-lg-top p-3 bg-white"
          
        >
          <Offcanvas.Body
            className="d-flex flex-column cavasBody"
          >
            <Link className="navbar-brand mb-3" href="/">
              <Image src={logo} alt="Instant Polls Logo" className="adminNavImg" />
            </Link>

            <div className="text-center highlightedColor">
              <strong>{user?.name}</strong>
              <p>{roleActingAs?.role?.name}</p>
            </div>

            <Nav className="nav-pills flex-column mb-auto">
              {sidebarLinks.map((link, index) => (
                <Nav.Item key={index}>
                  {link.label === 'Logout' ? (
                    <a href="#" className="nav-link p-1" onClick={link.onClick}>
                      <i className={link.icon}></i>
                      <span className="ms-3 nav-text">{link.label}</span>
                    </a>
                  ) : (
                    <Link
                      href={link.href}
                      className={`nav-link p-1 ${isActive(link.href) ? 'customer active' : ''}`}
                      onClick={handleSidebarClose}
                    >
                      <i className={link.icon}></i>
                      <span className="ms-3 nav-text">{link.label}</span>
                    </Link>
                  )}
                </Nav.Item>
              ))}
            </Nav>
          </Offcanvas.Body>
        </Offcanvas>

        {/* Content Area */}
        <div id="content" className="flex-grow-1">
          <div className="container-fluid p-3 p-lg-4" ref={pageRef}>
            {React.cloneElement(children)}
          </div>

          {/* Toast Messages */}
          <ToastContainer position="top-end" className="p-3 z-index-top">
            {toasts?.map((toast, index) => (
              <ToastMessage
                key={index}
                onClose={() => removeToast(toast.id)}
                message={toast.message}
                variant={toast.variant}
              />
            ))}
          </ToastContainer>
        </div>
      </section>
      {roleActingAs?.entity && !roleActingAs?.entity?.completed_onboarding_steps &&
        <ToastContainer position="bottom-end" className="p-3 z-index-top">
          <Toast onClose={() => setShowOnboarding(false)} show={showOnboarding} delay={10000}>
            <Toast.Header>
              <strong className="me-auto">Complete onboarding checklist</strong>
            </Toast.Header>
            <Toast.Body>
              <CustomerOnboarding />
            </Toast.Body>
          </Toast>
        </ToastContainer>
      }
    </div>
  );
};

export default AdminProvider;
